import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Percent, Lock, Unlock, CheckCircle2, FileText, History, Wallet, RefreshCw, X, Users } from 'lucide-react';
import { colors, API, fmt, authHeaders, podeGerenciarCartoes, isAdmin } from '../utils/theme';
import { PageHeader, Badge, Button, LoadingSpinner, Modal, EmptyState } from '../components/UI';

// Remuneração do Investidor de Cartão (13/09/2026).
// Regra (lib/remuneracao.js no server, #73): rendimento = taxa × limite
// comprometido de cada cartão (uso ao fim da competência, entre fatura +
// parcelado futuro e o limite do cartão). REVISAR mostra o motivo e pede
// observação antes de fechar.
const mesAtual = () => new Date().toISOString().slice(0, 7);
const pct = (v) => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 3 })}%`);
const dataBR = (d) => (d ? `${String(d).slice(8, 10)}/${String(d).slice(5, 7)}/${String(d).slice(0, 4)}` : '—');
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const mesBR = (m) => (m ? `${MESES[Number(String(m).slice(5, 7)) - 1]}/${String(m).slice(0, 4)}` : '—');

async function api(path, opts = {}) {
  const r = await fetch(`${API}/remuneracao${path}`, {
    ...opts, headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}

function Kpi({ label, valor, cor, sub }) {
  return (
    <div style={{ flex: '1 1 150px', minWidth: 140, padding: 14, borderRadius: 10,
      background: colors.card, border: `1px solid ${colors.border}` }}>
      <div style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: cor || colors.text, marginTop: 4 }}>{valor}</div>
      {sub && <div style={{ fontSize: 11, color: colors.textDim, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const STATUS_COR = { OK: colors.success, REVISAR: colors.danger, PENDENTE: colors.warning, FECHADO: colors.info, PAGO: colors.success };
const inp = {
  padding: '5px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.bg,
  color: colors.text, fontSize: 13, boxSizing: 'border-box', width: '100%',
};
const thS = { padding: '8px 10px', textAlign: 'left', color: colors.textMuted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tdS = { padding: '6px 10px', color: colors.text, fontSize: 13, verticalAlign: 'middle' };

// Input numérico que só grava ao sair do campo (blur/Enter) — evita PUT a cada tecla.
function CampoNum({ value, onCommit, disabled, placeholder, align = 'right', width = 120 }) {
  const [v, setV] = useState(value == null ? '' : String(value));
  useEffect(() => { setV(value == null ? '' : String(value)); }, [value]);
  const commit = () => {
    const atual = value == null ? '' : String(value);
    if (v === atual) return;
    onCommit(v === '' ? '' : Number(String(v).replace(',', '.')));
  };
  return (
    <input type="text" inputMode="decimal" value={v} disabled={disabled} placeholder={placeholder}
      onChange={e => setV(e.target.value)} onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      style={{ ...inp, width, textAlign: align, opacity: disabled ? 0.7 : 1 }} />
  );
}
function CampoTexto({ value, onCommit, disabled, placeholder }) {
  const [v, setV] = useState(value || '');
  useEffect(() => { setV(value || ''); }, [value]);
  return (
    <input type="text" value={v} disabled={disabled} placeholder={placeholder}
      onChange={e => setV(e.target.value)} onBlur={() => { if (v !== (value || '')) onCommit(v); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      style={{ ...inp, minWidth: 160, opacity: disabled ? 0.7 : 1 }} />
  );
}

// ── Demonstrativo (impressão / salvar PDF pelo navegador) ────────────────────
function htmlDemonstrativo(fe) {
  const linha = (c) => `<tr>
    <td>${[c.banco, c.final ? `final ${c.final}` : '', c.nome_cartao].filter(Boolean).join(' · ')}</td>
    <td>${dataBR(c.data_vencimento)}</td>
    <td class="n">${fmt(c.valor_fatura)}</td>
    <td class="n">${fmt(c.saldo_parcelado_futuro)}</td>
    <td class="n">${fmt(c.base_cartao)}</td>
    <td class="n"><b>${fmt(c.limite_comprometido ?? c.base_cartao)}</b></td>
    <td class="n">${fmt(c.rendimento_cartao)}</td>
    <td class="n">${c.limite_utilizado_informado != null ? fmt(c.limite_utilizado_informado) : '—'}</td>
    <td class="n">${c.divergencia != null ? fmt(c.divergencia) : '—'}</td>
    <td>${c.status}${(c.motivos || []).length ? ` (${c.motivos.join('; ')})` : ''}${c.observacao ? ` — ${c.observacao}` : ''}</td>
  </tr>`;
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>Fechamento ${fe.titular_nome} ${fe.competencia}</title>
  <style>
    body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:32px;font-size:13px}
    h1{font-size:20px;margin:0 0 4px} h2{font-size:14px;margin:22px 0 8px;text-transform:uppercase;letter-spacing:.4px;color:#444}
    .sub{color:#666} .destaque{border:2px solid #111;border-radius:10px;padding:14px 18px;margin:18px 0;display:flex;justify-content:space-between;align-items:center}
    .destaque .v{font-size:28px;font-weight:800} table{width:100%;border-collapse:collapse;margin-top:6px}
    th,td{border-bottom:1px solid #ddd;padding:6px 8px;text-align:left;vertical-align:top} th{background:#f3f3f3;font-size:11px;text-transform:uppercase}
    td.n,th.n{text-align:right;white-space:nowrap} tfoot td{font-weight:700} .box{background:#f7f7f7;border-radius:8px;padding:10px 12px}
    @media print{body{margin:14mm}}
  </style></head><body>
  <h1>Fechamento — Investidor de Cartão</h1>
  <div class="sub">Competência ${mesBR(fe.competencia)} · Investidor: <b>${fe.titular_nome}</b> · Taxa aplicada: <b>${pct(fe.taxa_pct)}</b></div>
  <div class="destaque"><div><div class="sub">Rendimento a pagar</div><div class="v">${fmt(fe.rendimento)}</div></div>
    <div style="text-align:right"><div class="sub">Limite comprometido total</div><div style="font-size:18px;font-weight:700">${fmt(fe.base_total)}</div>
    <div class="sub" style="margin-top:6px">Pagamento em <b>${dataBR(fe.data_pagamento)}</b>${fe.status === 'PAGO' ? ` · pago em ${dataBR(fe.pago_em)}` : ''}</div></div></div>
  <h2>Regra de cálculo</h2>
  <div class="box">base do cartão = valor da fatura + saldo parcelado em faturas futuras · limite comprometido = uso do cartão ao fim da competência,
  nunca abaixo da base nem acima do limite do cartão · rendimento = soma dos limites comprometidos × ${pct(fe.taxa_pct)}. A taxa incide sobre o total; o valor por cartão é demonstrativo e pode divergir em centavos.</div>
  <h2>Por cartão</h2>
  <table><thead><tr><th>Cartão</th><th>Vencimento</th><th class="n">Fatura</th><th class="n">Saldo parc. futuro</th><th class="n">Base</th><th class="n">Limite comprometido</th><th class="n">${pct(fe.taxa_pct)} demonstr.</th><th class="n">Limite informado</th><th class="n">Divergência</th><th>Conferência</th></tr></thead>
  <tbody>${(fe.itens || []).map(linha).join('')}</tbody>
  <tfoot><tr><td colspan="5">Total</td><td class="n">${fmt(fe.base_total)}</td><td class="n">${fmt(fe.rendimento)}</td><td colspan="3"></td></tr></tfoot></table>
  <h2>Conferência com o limite informado</h2>
  <div class="box">Divergência = limite comprometido − limite utilizado informado na fatura. Acima de 5% do comprometido exige justificativa (ex.: cartões que compartilham limite único no mesmo banco fecham em datas diferentes). O valor pago é sempre o limite comprometido; o limite informado serve só para conferência.</div>
  <h2>Procedimento</h2>
  <div class="box">1. Conferir os valores acima com os PDFs das faturas de origem. 2. Pagar o rendimento ao investidor na data indicada. 3. Registrar o pagamento no Finance (Remuneração → Marcar como pago) com o comprovante.</div>
  <div class="sub" style="margin-top:26px">Fechado por ${fe.fechado_por || '—'} em ${fe.fechado_em ? new Date(fe.fechado_em).toLocaleString('pt-BR') : '—'} · Finance XM Controle</div>
  </body></html>`;
}
function imprimirDemonstrativo(fe) {
  const w = window.open('', '_blank');
  if (!w) { alert('O navegador bloqueou a janela de impressão — libere pop-ups pra este site.'); return; }
  w.document.write(htmlDemonstrativo(fe)); w.document.close();
  setTimeout(() => { w.focus(); w.print(); }, 300);
}

// ── Bloco de um investidor na apuração ───────────────────────────────────────
function InvestidorBloco({ inv, mes, podeEditar, onChange, onDemonstrativo }) {
  const fe = inv.fechamento;
  const linhas = fe ? fe.itens : inv.cartoes;
  const editavel = podeEditar && !fe;
  const [busy, setBusy] = useState(false);

  const salvarCampo = async (row, campo, valor) => {
    const body = {
      data_vencimento: row.data_vencimento || null,
      valor_fatura: row.valor_fatura_fonte === 'manual' ? row.valor_fatura : '',
      saldo_parcelado_futuro: row.saldo_parcelado_futuro ?? 0,
      limite_utilizado_informado: row.limite_utilizado_informado ?? '',
      observacao: row.observacao || '',
    };
    body[campo] = valor;
    setBusy(true);
    try { await api(`/faturas/${row.cartao_id}/${mes}`, { method: 'PUT', body: JSON.stringify(body) }); onChange(); }
    catch (e) { alert('Erro ao salvar: ' + e.message); }
    setBusy(false);
  };
  const acao = async (rota, body, confirmar) => {
    if (confirmar && !window.confirm(confirmar)) return;
    setBusy(true);
    try { await api(rota, { method: 'POST', body: JSON.stringify({ titular_id: inv.titular_id, mes, ...body }) }); onChange(); }
    catch (e) { alert(e.message); }
    setBusy(false);
  };
  const marcarPago = () => {
    const d = window.prompt('Data do pagamento (AAAA-MM-DD):', new Date().toISOString().slice(0, 10));
    if (d == null) return;
    const comp = window.prompt('Comprovante / observação (opcional):', '') || '';
    acao('/pagar', { data_pagamento: d, comprovante: comp });
  };

  const statusGeral = fe ? fe.status : (inv.pendentes ? 'PENDENTE' : (inv.cartoes.some(c => c.status === 'REVISAR') ? 'REVISAR' : 'OK'));
  const base = fe ? fe.base_total : inv.base_total;
  const rend = fe ? fe.rendimento : inv.rendimento;
  const dataPag = fe ? fe.data_pagamento : inv.data_pagamento;
  const motivo = !inv.pode_fechar
    ? [inv.pendentes ? `${inv.pendentes} sem valor de fatura` : '', inv.revisar_sem_obs ? `${inv.revisar_sem_obs} em REVISAR sem observação` : ''].filter(Boolean).join(' · ')
    : 'Fecha a competência, guarda o snapshot e cria a conta a pagar';

  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 16px', borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: colors.text, minWidth: 180 }}>{inv.nome}</div>
        <Badge text={statusGeral === 'OK' ? 'APURADO' : statusGeral} color={STATUS_COR[statusGeral] || colors.textMuted} />
        <div style={{ fontSize: 12, color: colors.textMuted }}>Taxa <b style={{ color: colors.text }}>{pct(fe ? fe.taxa_pct : inv.taxa_pct)}</b></div>
        <div style={{ fontSize: 12, color: colors.textMuted }}>Limite comprometido <b style={{ color: colors.text }}>{fmt(base)}</b></div>
        <div style={{ fontSize: 12, color: colors.textMuted }}>Rendimento <b style={{ color: colors.success, fontSize: 15 }}>{fmt(rend)}</b></div>
        <div style={{ fontSize: 12, color: colors.textMuted }}>Pagamento em <b style={{ color: colors.text }}>{dataBR(dataPag)}</b>{fe?.status === 'PAGO' && ` · pago ${dataBR(fe.pago_em)}`}</div>
        <div style={{ flex: 1 }} />
        {podeEditar && !fe && (
          <Button size="sm" icon={Lock} color={colors.primary} onClick={() => acao('/fechar', { rendimento_esperado: rend }, `Fechar ${mesBR(mes)} de ${inv.nome}: rendimento ${fmt(rend)} sobre limite comprometido de ${fmt(base)}?`)}
            disabled={busy || !inv.pode_fechar} title={motivo}>Fechar competência</Button>
        )}
        {podeEditar && fe && fe.status === 'FECHADO' && (<>
          <Button size="sm" icon={CheckCircle2} color={colors.success} onClick={marcarPago} disabled={busy}>Marcar como pago</Button>
          <Button size="sm" icon={Unlock} variant="outline" color={colors.warning} onClick={() => acao('/reabrir', {}, 'Reabrir a competência? A conta a pagar será cancelada.')} disabled={busy}>Reabrir</Button>
        </>)}
        {fe && <Button size="sm" icon={FileText} variant="outline" color={colors.info} onClick={() => onDemonstrativo(fe.id)}>Demonstrativo</Button>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
            <th style={thS}>Cartão</th><th style={thS}>Venc.</th>
            <th style={{ ...thS, textAlign: 'right' }}>Valor da fatura</th>
            <th style={{ ...thS, textAlign: 'right' }}>Saldo parc. futuro</th>
            <th style={{ ...thS, textAlign: 'right' }}>Base</th>
            <th style={{ ...thS, textAlign: 'right' }}>Limite comprometido</th>
            <th style={{ ...thS, textAlign: 'right' }}>Taxa</th>
            <th style={{ ...thS, textAlign: 'right' }}>Rendimento</th>
            <th style={{ ...thS, textAlign: 'right' }}>Limite informado</th>
            <th style={{ ...thS, textAlign: 'right' }}>Divergência</th>
            <th style={thS}>Conferência</th><th style={thS}>Observação</th>
          </tr></thead>
          <tbody>
            {linhas.map(c => (
              <tr key={c.cartao_id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                <td style={tdS}>
                  <div style={{ fontWeight: 600 }}>{c.nome_cartao}</div>
                  <div style={{ fontSize: 11, color: colors.textDim }}>{[c.banco, c.final ? `final ${c.final}` : ''].filter(Boolean).join(' · ') || '—'}</div>
                </td>
                <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{dataBR(c.data_vencimento)}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>
                  {editavel ? <CampoNum value={c.valor_fatura} placeholder="sem fatura" onCommit={v => salvarCampo(c, 'valor_fatura', v)} /> : fmt(c.valor_fatura)}
                  <div style={{ fontSize: 10, color: c.valor_fatura_fonte === 'manual' ? colors.warning : colors.textDim, marginTop: 2 }}
                    title={c.valor_fatura_sistema != null ? `Sistema: ${fmt(c.valor_fatura_sistema)}` : 'Sem fatura carregada neste mês — digite 0 se não houve uso'}>
                    {c.valor_fatura_fonte === 'manual' ? 'ajustado à mão' : c.valor_fatura_fonte === 'fatura' ? 'da fatura' : c.valor_fatura_fonte === 'ocr' ? 'do OCR' : 'sem fatura'}
                  </div>
                </td>
                <td style={{ ...tdS, textAlign: 'right' }}>
                  {editavel ? <CampoNum value={c.saldo_parcelado_futuro} onCommit={v => salvarCampo(c, 'saldo_parcelado_futuro', v === '' ? 0 : v)} /> : fmt(c.saldo_parcelado_futuro)}
                </td>
                <td style={{ ...tdS, textAlign: 'right' }}>{c.base_cartao != null ? fmt(c.base_cartao) : '—'}</td>
                <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}
                  title={c.uso_sistema > c.limite_total && c.limite_total > 0 ? `Uso lançado ${fmt(c.uso_sistema)} passa do limite do cartão: confira e justifique` : undefined}>
                  {(c.limite_comprometido ?? c.base_cartao) != null ? fmt(c.limite_comprometido ?? c.base_cartao) : '—'}
                </td>
                <td style={{ ...tdS, textAlign: 'right', color: colors.textMuted }}>{pct(c.taxa_pct)}</td>
                <td style={{ ...tdS, textAlign: 'right', color: colors.success }}>{c.rendimento_cartao != null ? fmt(c.rendimento_cartao) : '—'}</td>
                <td style={{ ...tdS, textAlign: 'right' }}>
                  {editavel ? <CampoNum value={c.limite_utilizado_informado} placeholder="—" onCommit={v => salvarCampo(c, 'limite_utilizado_informado', v)} />
                    : (c.limite_utilizado_informado != null ? fmt(c.limite_utilizado_informado) : '—')}
                </td>
                <td style={{ ...tdS, textAlign: 'right', color: c.status === 'REVISAR' ? colors.danger : colors.textMuted }}>
                  {c.divergencia != null ? `${fmt(c.divergencia)} (${(c.divergencia_pct * 100).toFixed(1)}%)` : '—'}
                </td>
                <td style={tdS}><Badge text={c.status} color={STATUS_COR[c.status] || colors.textMuted} />
                  {(c.motivos || []).map(m => <div key={m} style={{ fontSize: 10, color: colors.danger, marginTop: 2 }}>{m}</div>)}
                </td>
                <td style={tdS}>
                  {editavel ? <CampoTexto value={c.observacao} placeholder={c.status === 'REVISAR' ? 'obrigatória pra fechar' : ''} onCommit={v => salvarCampo(c, 'observacao', v)} /> : (c.observacao || '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Aba Taxas ────────────────────────────────────────────────────────────────
function AbaTaxas({ podeEditar }) {
  const [data, setData] = useState(null);
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(null);
  const carregar = useCallback(async () => {
    try { setData(await api('/taxas')); setErro(''); } catch (e) { setErro(e.message); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  const salvarTitular = async (t, v, extra) => {
    // Apagar o número do campo é um gesto de edição comum, e antes ele
    // removia a taxa com um aviso que só falava da apuração. O efeito real é
    // maior: sem taxa, o investidor SOME da aba Investidores — junto com o
    // acesso ao cadastro e aos contratos dele. Quem some assim parece ter sido
    // apagado do sistema, e não foi.
    if (v === '') {
      const ok = window.confirm(
        `Remover a taxa de ${t.nome}?\n\n`
        + '• Ele deixa de entrar na apuração da remuneração.\n'
        + '• Ele SAI da lista de Investidores (cadastro e contratos deixam de aparecer ali).\n'
        + '• Os cartões, o cadastro e os contratos continuam salvos — basta cadastrar a taxa de novo pra ele voltar.\n\n'
        + 'Se você só queria corrigir o valor, cancele e digite a nova taxa por cima.');
      if (!ok) return;
    }
    try {
      if (v === '') await api(`/taxas/${t.id}`, { method: 'DELETE' });
      else await api(`/taxas/${t.id}`, { method: 'PUT', body: JSON.stringify({ taxa_pct: v, ...(extra || {}) }) });
      carregar();
    } catch (e) { alert(e.message); carregar(); }
  };
  const salvarCartao = async (c, v) => {
    try { await api(`/taxas/cartao/${c.id}`, { method: 'PUT', body: JSON.stringify({ taxa_pct: v === '' ? null : v }) }); carregar(); }
    catch (e) { alert(e.message); carregar(); }
  };
  if (erro) return <div style={{ color: colors.danger }}>{erro}</div>;
  if (!data) return <LoadingSpinner />;
  const cartoesDe = (tid) => data.cartoes.filter(c => c.titular_id === tid);
  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', fontSize: 12, color: colors.textMuted, borderBottom: `1px solid ${colors.border}` }}>
        Só titular <b style={{ color: colors.text }}>com taxa</b> entra na apuração. Taxa em % ao mês sobre o limite comprometido de cada cartão.
        Cartão pode ter taxa própria; em branco herda a do titular. Padrão sugerido: {pct(data.taxa_padrao_pct)}.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
          <th style={thS}>Titular</th><th style={thS}>Cartões apuráveis</th><th style={{ ...thS, textAlign: 'right' }}>Taxa (% a.m.)</th><th style={thS}>Modalidade</th><th style={thS}>Pontos</th><th style={thS}>Situação</th><th style={thS}>Atualizado</th>
        </tr></thead>
        <tbody>
          {data.titulares.map(t => (
            <React.Fragment key={t.id}>
              <tr style={{ borderBottom: `1px solid ${colors.border}22`, cursor: t.investidor ? 'pointer' : 'default' }}
                onClick={() => t.investidor && setAberto(aberto === t.id ? null : t.id)}>
                <td style={{ ...tdS, fontWeight: 600 }}>{t.nome}</td>
                <td style={tdS}>{t.qtd_cartoes}</td>
                <td style={{ ...tdS, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                  <CampoNum value={t.taxa_pct} placeholder="ex: 1,15" width={100} disabled={!podeEditar} onCommit={v => salvarTitular(t, v)} />
                </td>
                <td style={tdS} onClick={e => e.stopPropagation()}>
                  <select value={t.modalidade || ''} disabled={!podeEditar || !t.investidor} onChange={e => salvarTitular(t, t.taxa_pct ?? taxaSugerida(e.target.value, t.pontos_organicos_investidor), { modalidade: e.target.value })}
                    style={{ ...inp, width: 230, fontSize: 12 }}>
                    <option value="">{t.investidor ? '—' : '(cadastre a taxa)'}</option>
                    {Object.entries(MODALIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </td>
                <td style={tdS} onClick={e => e.stopPropagation()}>
                  {t.investidor && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: colors.textMuted, cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!t.pontos_organicos_investidor} disabled={!podeEditar}
                        onChange={e => salvarTitular(t, t.taxa_pct ?? taxaSugerida(t.modalidade, e.target.checked), { pontos_organicos_investidor: e.target.checked })} />
                      pontos c/ investidor
                    </label>
                  )}
                </td>
                <td style={tdS}>{t.investidor ? <Badge text="INVESTIDOR" color={colors.success} /> : <span style={{ color: colors.textDim, fontSize: 12 }}>fora da apuração</span>}</td>
                <td style={{ ...tdS, fontSize: 11, color: colors.textDim }}>{t.atualizado_por ? `${t.atualizado_por} · ${new Date(t.atualizado_em).toLocaleDateString('pt-BR')}` : '—'}</td>
              </tr>
              {aberto === t.id && cartoesDe(t.id).map(c => (
                <tr key={`c${c.id}`} style={{ background: `${colors.primary}08` }}>
                  <td style={{ ...tdS, paddingLeft: 32, fontSize: 12 }}>↳ {c.nome_cartao}</td>
                  <td style={{ ...tdS, fontSize: 11, color: colors.textDim }}>{[c.banco, c.final ? `final ${c.final}` : ''].filter(Boolean).join(' · ')}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>
                    <CampoNum value={c.taxa_pct} placeholder={`herda ${pct(t.taxa_pct)}`} width={110} disabled={!podeEditar} onCommit={v => salvarCartao(c, v)} />
                  </td>
                  <td style={{ ...tdS, fontSize: 11, color: colors.textDim }} colSpan={4}>{c.taxa_pct != null ? 'taxa própria do cartão' : 'herda a taxa do titular'}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Aba Histórico ────────────────────────────────────────────────────────────
const PALETA = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ef4444', '#84cc16', '#f97316'];
function AbaHistorico({ podeEditar, onDemonstrativo, onChange }) {
  const [data, setData] = useState(null);
  const [erro, setErro] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fTitular, setFTitular] = useState('');
  const carregar = useCallback(async () => {
    try { setData(await api('/fechamentos?meses=12')); setErro(''); } catch (e) { setErro(e.message); }
  }, []);
  useEffect(() => { carregar(); }, [carregar]);
  const lista = useMemo(() => (data?.fechamentos || []).filter(f => (!fStatus || f.status === fStatus) && (!fTitular || String(f.titular_id) === fTitular)), [data, fStatus, fTitular]);
  const titulares = useMemo(() => { const m = new Map(); (data?.fechamentos || []).forEach(f => m.set(f.titular_id, f.titular_nome)); return [...m.entries()]; }, [data]);
  const serie = useMemo(() => {
    const porMes = new Map();
    (data?.fechamentos || []).forEach(f => {
      const m = porMes.get(f.competencia) || { competencia: f.competencia, total: 0, por: {} };
      m.total += f.rendimento; m.por[f.titular_nome] = (m.por[f.titular_nome] || 0) + f.rendimento; porMes.set(f.competencia, m);
    });
    return [...porMes.values()].sort((a, b) => a.competencia.localeCompare(b.competencia));
  }, [data]);
  const maxMes = Math.max(1, ...serie.map(s => s.total));
  const corDe = (nome) => PALETA[titulares.findIndex(([, n]) => n === nome) % PALETA.length] || colors.primary;
  const marcarPago = async (f) => {
    const d = window.prompt('Data do pagamento (AAAA-MM-DD):', new Date().toISOString().slice(0, 10));
    if (d == null) return;
    const comp = window.prompt('Comprovante / observação (opcional):', '') || '';
    try { await api('/pagar', { method: 'POST', body: JSON.stringify({ titular_id: f.titular_id, mes: f.competencia, data_pagamento: d, comprovante: comp }) }); carregar(); onChange(); }
    catch (e) { alert(e.message); }
  };
  if (erro) return <div style={{ color: colors.danger }}>{erro}</div>;
  if (!data) return <LoadingSpinner />;
  const pago = lista.filter(f => f.status === 'PAGO').reduce((s, f) => s + f.rendimento, 0);
  const aPagar = lista.filter(f => f.status === 'FECHADO').reduce((s, f) => s + f.rendimento, 0);
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <Kpi label="Pago (12 meses)" valor={fmt(pago)} cor={colors.success} />
        <Kpi label="Fechado, a pagar" valor={fmt(aPagar)} cor={colors.info} />
        <Kpi label="Fechamentos" valor={lista.length} sub={`${titulares.length} investidor(es)`} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={fTitular} onChange={e => setFTitular(e.target.value)} style={{ ...inp, width: 200 }}>
            <option value="">Todos os investidores</option>
            {titulares.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
          </select>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ ...inp, width: 150 }}>
            <option value="">Todos os status</option><option value="FECHADO">Fechado</option><option value="PAGO">Pago</option>
          </select>
        </div>
      </div>
      {serie.length > 0 && (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: colors.textMuted, marginBottom: 8 }}>RENDIMENTO POR COMPETÊNCIA (empilhado por investidor)</div>
          {serie.map(s => (
            <div key={s.competencia} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <div style={{ width: 64, fontSize: 12, color: colors.textMuted }}>{mesBR(s.competencia)}</div>
              <div style={{ flex: 1, display: 'flex', height: 16, borderRadius: 4, overflow: 'hidden', background: `${colors.border}55` }}>
                {Object.entries(s.por).map(([nome, v]) => (
                  <div key={nome} title={`${nome}: ${fmt(v)}`} style={{ width: `${(v / maxMes) * 100}%`, background: corDe(nome) }} />
                ))}
              </div>
              <div style={{ width: 110, textAlign: 'right', fontSize: 12, fontWeight: 700, color: colors.text }}>{fmt(s.total)}</div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
            {titulares.map(([id, nome]) => (
              <span key={id} style={{ fontSize: 11, color: colors.textMuted, display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: corDe(nome), display: 'inline-block' }} />{nome}
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {lista.length === 0 ? <EmptyState message="Nenhum fechamento no período" /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              <th style={thS}>Competência</th><th style={thS}>Investidor</th><th style={thS}>Cartões</th>
              <th style={{ ...thS, textAlign: 'right' }}>Taxa</th><th style={{ ...thS, textAlign: 'right' }}>Base</th>
              <th style={{ ...thS, textAlign: 'right' }}>Rendimento</th><th style={thS}>Pagamento</th><th style={thS}>Status</th><th style={thS}>Ações</th>
            </tr></thead>
            <tbody>
              {lista.map(f => (
                <tr key={f.id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                  <td style={tdS}>{mesBR(f.competencia)}</td>
                  <td style={{ ...tdS, fontWeight: 600 }}>{f.titular_nome}</td>
                  <td style={tdS}>{f.qtd_cartoes}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{pct(f.taxa_pct)}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{fmt(f.base_total)}</td>
                  <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: colors.success }}>{fmt(f.rendimento)}</td>
                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{dataBR(f.data_pagamento)}{f.status === 'PAGO' && <div style={{ fontSize: 10, color: colors.textDim }}>pago {dataBR(f.pago_em)}</div>}</td>
                  <td style={tdS}><Badge text={f.status} color={STATUS_COR[f.status] || colors.textMuted} /></td>
                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                    <Button size="sm" variant="outline" icon={FileText} color={colors.info} onClick={() => onDemonstrativo(f.id)}>Demonstrativo</Button>
                    {podeEditar && f.status === 'FECHADO' && (
                      <span style={{ marginLeft: 6 }}><Button size="sm" icon={CheckCircle2} color={colors.success} onClick={() => marcarPago(f)}>Pago</Button></span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── D4Sign: configuração/teste e contratos por investidor (15/09/2026) ──────
async function apiD4(path, opts = {}) {
  const r = await fetch(`${API}/d4sign${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) } });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
  return d;
}
const D4_COR = { ENVIADO: colors.info, ENVIANDO: colors.warning, ASSINADO: colors.success, CANCELADO: colors.textMuted, ERRO: colors.danger };
function BlocoD4Sign({ podeEditar }) {
  const [st, setSt] = useState(null);
  const [teste, setTeste] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiD4('/status').then(setSt).catch(() => setSt({ configurado: false })); }, []);
  const testar = async () => {
    setBusy(true); setTeste(null);
    try { setTeste(await apiD4('/cofres')); } catch (e) { setTeste({ ok: false, error: e.message }); }
    setBusy(false);
  };
  if (!st || !podeEditar) return null;
  return (
    <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 700, color: colors.text }}>Assinatura eletrônica (D4Sign)</div>
        <Badge text={st.configurado ? `CONFIGURADA · ${String(st.ambiente).toUpperCase()}` : 'NÃO CONFIGURADA'} color={st.configurado ? (st.ambiente === 'producao' ? colors.success : colors.warning) : colors.textMuted} />
        <span style={{ flex: 1 }} />
        {st.configurado && <Button size="sm" variant="outline" onClick={testar} disabled={busy}>{busy ? 'Testando…' : 'Testar conexão'}</Button>}
      </div>
      <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 8, lineHeight: 1.6 }}>
        {st.configurado ? (<>
          Cofre: <code>{st.cofre || 'automático (primeiro cofre da conta)'}</code>
          <br />Assinam pela XMiles: <b>{(st.signatarios?.xmiles || []).join(', ') || 'automático (e-mail de quem envia)'}</b>
          {(st.signatarios?.avalistas || []).length > 0 && <> · avalista: <b>{st.signatarios.avalistas.join(', ')}</b></>}
          <br />Testemunhas: <b>{(st.signatarios?.testemunhas || []).join(', ') || '— (nenhuma)'}</b>
          <br />Ordem: <b>{st.ordem_assinatura === 'sequencial' ? 'em sequência (um só é avisado depois que o anterior assina)' : 'simultânea (todos recebem o e-mail de uma vez)'}</b>
          {st.ordem_assinatura !== 'sequencial' && <> · para mudar, defina <code>D4SIGN_ORDEM_ASSINATURA=sequencial</code></>}
          <br />Webhook pra cadastrar no painel da D4Sign (ou é registrado por documento, automaticamente): <code>{st.webhook_url}</code>
          {st.cofre_automatico && (
            <div style={{ marginTop: 6, padding: 8, borderRadius: 6, background: `${colors.warning}14`, border: `1px solid ${colors.warning}55`, color: colors.text }}>
              <b>Sem cofre definido.</b> Os contratos vão para o primeiro cofre da conta, que é o mesmo dos contratos Diamante do TechMiles. Crie um cofre só para o Finance no painel da D4Sign e defina <code>D4SIGN_SAFE_UUID</code> — o cofre é grátis e não muda a credencial.
            </div>
          )}
          {st.ambiente === 'producao' && (
            <div style={{ marginTop: 6, padding: 8, borderRadius: 6, background: `${colors.danger}12`, border: `1px solid ${colors.danger}44`, color: colors.text }}>
              <b>Produção.</b> Toda assinatura aqui tem validade jurídica. A conta é a mesma do TechMiles: cada contrato enviado consome um envio da cota compartilhada, e envios em lote derrubam os dois sistemas por alguns minutos. Mande um de cada vez, com pausa.
            </div>
          )}
          <br />Corrigir e-mail errado e reenviar convite <b>não gastam cota</b>. Use os botões na lista em vez de refazer o contrato.
        </>) : (<>
          Obrigatória no Railway: <code>D4SIGN_TOKEN</code> — no painel da D4Sign, menu <b>Dev API</b>. Opcionais: <code>D4SIGN_CRYPT_KEY</code> (só se a sua conta tiver crypt key habilitada), <code>D4SIGN_SAFE_UUID</code> (senão usa o primeiro cofre da conta), <code>D4SIGN_SIGNATARIO_XMILES</code> (senão o e-mail de quem envia), <code>D4SIGN_SIGNATARIO_AVALISTA</code> e <code>D4SIGN_TESTEMUNHAS</code> — estas três aceitam vários e-mails separados por vírgula. <code>D4SIGN_AMBIENTE=sandbox</code> (padrão) usa o ambiente de testes; <code>producao</code> assina de verdade.
        </>)}
      </div>
      {teste && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 8, fontSize: 12, background: teste.ok ? `${colors.success}12` : `${colors.danger}12`, border: `1px solid ${teste.ok ? colors.success : colors.danger}44`, color: colors.text }}>
          {teste.ok ? (<>
            Conexão ok ({teste.ambiente}). Cofres da conta:
            <ul style={{ margin: '6px 0 0 18px' }}>{(teste.cofres || []).map((c, i) => <li key={i}><b>{c.nome || '(sem nome)'}</b> — uuid <code>{c.uuid}</code></li>)}</ul>
            {(!teste.cofres || !teste.cofres.length) && <div>Nenhum cofre listado — crie um no painel da D4Sign. Resposta bruta: <code>{JSON.stringify(teste.bruto || teste).slice(0, 300)}</code></div>}
          </>) : <>Falhou: {teste.error}</>}
        </div>
      )}
    </div>
  );
}
function ContratosD4Sign({ titularId, podeEditar }) {
  const [lista, setLista] = useState(null);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const carregar = useCallback(async () => {
    try { const d = await apiD4(`/contratos?titular_id=${titularId}`); setLista(d.contratos || []); } catch (e) { setLista([]); }
  }, [titularId]);
  useEffect(() => { carregar(); apiD4('/status').then(setSt).catch(() => setSt({ configurado: false })); }, [carregar]);
  const enviar = async () => {
    if (!window.confirm('Gerar o contrato preenchido e enviar pra assinatura na D4Sign?')) return;
    setBusy(true);
    try { await apiD4('/contratos', { method: 'POST', body: JSON.stringify({ titular_id: titularId }) }); await carregar(); }
    catch (e) { alert('D4Sign: ' + e.message); await carregar(); }
    setBusy(false);
  };
  const atualizar = async (c) => { setBusy(true); try { await apiD4(`/contratos/${c.id}/atualizar`, { method: 'POST' }); await carregar(); } catch (e) { alert(e.message); } setBusy(false); };
  // Reenviar e trocar e-mail não gastam cota da conta D4Sign. E-mail digitado
  // errado é o motivo mais comum de contrato parado — refazer seria jogar um
  // envio fora, e a conta é compartilhada com os contratos do TechMiles.
  const reenviar = async (c) => {
    const pendente = (c.signatarios || []).find(s => s.papel === 'INVESTIDOR');
    const email = window.prompt('Reenviar o convite de assinatura para qual e-mail já cadastrado no documento?', pendente?.email || '');
    if (email === null) return;
    setBusy(true);
    try { const r = await apiD4(`/contratos/${c.id}/reenviar`, { method: 'POST', body: JSON.stringify({ email: email.trim() }) }); alert('Convite reenviado para ' + r.reenviado_para); }
    catch (e) { alert('D4Sign: ' + e.message); }
    setBusy(false);
  };
  const trocarEmail = async (c) => {
    const atual = window.prompt('Trocar o e-mail de qual signatário? (o e-mail errado, como está hoje)', (c.signatarios || [])[0]?.email || '');
    if (atual === null) return;
    const novo = window.prompt('E-mail correto:', '');
    if (novo === null || !novo.trim()) return;
    setBusy(true);
    try { await apiD4(`/contratos/${c.id}/trocar-email`, { method: 'POST', body: JSON.stringify({ de: atual.trim(), para: novo.trim() }) }); alert('E-mail trocado e convite reenviado. Não gastou nenhum envio da conta.'); await carregar(); }
    catch (e) { alert('D4Sign: ' + e.message); }
    setBusy(false);
  };
  const cancelar = async (c) => { if (!window.confirm('Cancelar este envio na D4Sign?')) return; try { await apiD4(`/contratos/${c.id}/cancelar`, { method: 'POST' }); await carregar(); } catch (e) { alert(e.message); } };
  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 10, padding: 12, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontWeight: 700, color: colors.text, fontSize: 13 }}>Contrato assinado (D4Sign)</div>
        <span style={{ flex: 1 }} />
        {podeEditar && st?.configurado && <Button size="sm" color={colors.primary} onClick={enviar} disabled={busy}>{busy ? 'Enviando…' : 'Enviar contrato pra assinatura'}</Button>}
        {st && !st.configurado && <span style={{ fontSize: 11, color: colors.textDim }}>D4Sign não configurada</span>}
      </div>
      {lista === null ? <LoadingSpinner /> : lista.length === 0 ? <div style={{ fontSize: 12, color: colors.textDim }}>Nenhum envio ainda.</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}><th style={thS}>Enviado</th><th style={thS}>Documento</th><th style={thS}>Status</th><th style={thS}>Signatários</th><th style={thS}>Ações</th></tr></thead>
          <tbody>{lista.map(c => (
            <tr key={c.id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
              <td style={{ ...tdS, whiteSpace: 'nowrap' }}>{new Date(c.enviado_em).toLocaleString('pt-BR')}<div style={{ fontSize: 10, color: colors.textDim }}>{c.ambiente} · {c.enviado_por}</div></td>
              <td style={tdS}>{c.nome_documento}<div style={{ fontSize: 10, color: colors.textDim }}>{c.uuid_documento || '—'}</div></td>
              <td style={tdS}><Badge text={c.status} color={D4_COR[c.status] || colors.textMuted} />{c.status_d4_nome && <div style={{ fontSize: 10, color: colors.textDim }}>{c.status_d4_nome}</div>}{c.erro && <div style={{ fontSize: 10, color: colors.danger }}>{c.erro}</div>}</td>
              <td style={{ ...tdS, fontSize: 11 }}>{c.signatarios.map(s => `${s.papel}: ${s.email}`).join(' · ')}</td>
              <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                {podeEditar && c.uuid_documento && c.status !== 'CANCELADO' && <Button size="sm" variant="outline" onClick={() => atualizar(c)} disabled={busy}>Atualizar</Button>}
                {c.url_download && <span style={{ marginLeft: 6 }}><a href={c.url_download} target="_blank" rel="noreferrer" style={{ color: colors.success, fontWeight: 600 }}>Baixar assinado</a></span>}
                {podeEditar && c.uuid_documento && c.status === 'ENVIADO' && <span style={{ marginLeft: 6 }}><Button size="sm" variant="outline" onClick={() => reenviar(c)} disabled={busy}>Reenviar convite</Button></span>}
                {podeEditar && c.uuid_documento && c.status === 'ENVIADO' && <span style={{ marginLeft: 6 }}><Button size="sm" variant="outline" onClick={() => trocarEmail(c)} disabled={busy}>Trocar e-mail</Button></span>}
                {podeEditar && c.uuid_documento && (c.status === 'ENVIADO' || c.status === 'ERRO') && <span style={{ marginLeft: 6 }}><Button size="sm" variant="outline" color={colors.danger} onClick={() => cancelar(c)}>Cancelar</Button></span>}
              </td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

// ── Aba Investidores: convite, cadastros pendentes, aprovação e termos ───────
const MODALIDADE_LABEL = {
  TAXA: 'Mútuo mercantil · % sobre a utilização',
  SCP: 'SCP · divide lucro e rateia despesa',
};
// Sugestão: pontos orgânicos com o investidor → 1%; com a operação → 2%.
const taxaSugerida = (modalidade, pontos) => (modalidade === 'SCP' ? '0' : (pontos ? '1' : '2'));
const pontosTxt = (inv) => (inv.pontos_organicos_investidor ? 'pontos orgânicos: investidor' : 'pontos orgânicos: operação');
const CAD_COR = { CONVIDADO: colors.warning, ENVIADO: colors.info, APROVADO: colors.success };
function AbaInvestidores({ podeEditar }) {
  const [lista, setLista] = useState(null);
  const [erro, setErro] = useState('');
  const [conv, setConv] = useState({ nome: '', email: '', telefone: '', modalidade: 'TAXA', taxa_pct: '2', pontos_organicos_investidor: false });
  const [convResult, setConvResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [aprov, setAprov] = useState({});
  const [operacaoAprov, setOperacaoAprov] = useState('');
  const [termos, setTermos] = useState({ TAXA: '', SCP: '' });
  const [termoInfo, setTermoInfo] = useState({});
  // Inativos escondidos por padrão: é o que mantém a lista do dia a dia
  // utilizável, sem precisar apagar ninguém pra limpar a tela.
  const [verInativos, setVerInativos] = useState(false);
  const carregar = useCallback(async () => {
    try {
      const d = await api(`/investidores${verInativos ? '?inativos=1' : ''}`);
      setLista(d.investidores || []); setErro('');
      const [tx, sc] = await Promise.all([api('/termo?grupo=TAXA'), api('/termo?grupo=SCP')]);
      setTermos({ TAXA: tx.texto, SCP: sc.texto }); setTermoInfo({ TAXA: tx, SCP: sc });
    } catch (e) { setErro(e.message); }
  }, [verInativos]);
  useEffect(() => { carregar(); }, [carregar]);

  const alternarAtivo = async (inv) => {
    const desativando = String(inv.status || 'ATIVO').toUpperCase() !== 'INATIVO';
    const msg = desativando
      ? `Desativar ${inv.nome}?\n\n• Sai da lista de investidores (marque "mostrar inativos" pra ver de novo).\n`
        + '• O acesso dele ao sistema é bloqueado.\n'
        + '• Nada é apagado: cadastro, cartões, contratos e histórico ficam.\n'
        + '• Cartões e remuneração NÃO são alterados — se ainda há saldo, a apuração continua.'
      : `Reativar ${inv.nome}? Ele volta para a lista e o acesso é religado.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api(`/investidores/${inv.id}/${desativando ? 'desativar' : 'reativar'}`, { method: 'POST' });
      alert(r.aviso);
      carregar();
    } catch (e) { alert(e.message); }
  };
  const convidar = async () => {
    setBusy(true); setConvResult(null);
    try {
      const r = await api('/convidar', { method: 'POST', body: JSON.stringify(conv) });
      setConvResult(r); setConv({ nome: '', email: '', telefone: '', modalidade: 'TAXA', taxa_pct: '2', pontos_organicos_investidor: false }); carregar();
    } catch (e) { alert(e.message); }
    setBusy(false);
  };
  const reenviar = async (inv) => {
    try { const r = await api(`/investidores/${inv.id}/reenviar-convite`, { method: 'POST' }); setConvResult({ ...r, nome: inv.nome }); carregar(); }
    catch (e) { alert(e.message); }
  };
  // Excluir de vez. Só ADMIN, e só quando não há movimento financeiro — a
  // simulação diz antes o que sai e o que trava, pra ninguém descobrir depois.
  // Existe porque não havia caminho: o botão de excluir usuário só desativa
  // (a linha fica) e a exclusão do titular trava justamente por causa dela.
  const excluirInvestidor = async (inv) => {
    let d;
    try { d = await api(`/investidores/${inv.id}/exclusao`); }
    catch (e) { alert(e.message); return; }
    if (d.bloqueio) { alert(d.bloqueio); return; }
    const resumo = [
      `Excluir DEFINITIVAMENTE o cadastro de ${d.titular.nome}?`, '',
      `Serão removidos: ${d.cartoes.length} cartão(ões), ${d.usuarios.length} login(s)`
        + (d.contratos_d4sign ? `, ${d.contratos_d4sign} contrato(s) registrado(s)` : '') + '.',
      'Não há movimento financeiro neste cadastro, então nada de contábil se perde.',
      d.contratos_d4sign ? 'Atenção: o documento na D4Sign NÃO é apagado — cancele lá se for o caso.' : '',
      '', 'Isto não tem volta. Digite o nome do investidor pra confirmar:',
    ].filter(Boolean).join('\n');
    const nome = window.prompt(resumo, '');
    if (nome === null) return;
    try {
      const r = await api(`/investidores/${inv.id}/excluir`, { method: 'POST', body: JSON.stringify({ confirmar_nome: nome }) });
      alert(`Cadastro de ${r.removidos.titular} excluído: ${r.removidos.cartoes} cartão(ões) e ${r.removidos.usuarios} login(s).`);
      carregar();
    } catch (e) { alert(e.message); }
  };
  // Investidor antigo não tem login. Não dá pra usar o convite comum: ele
  // criaria um titular novo e duplicaria a pessoa.
  const criarAcesso = async (inv) => {
    const email = inv.email || window.prompt(`E-mail de ${inv.nome} para o login e o link de acesso:`, '');
    if (email === null) return;
    if (!String(email || '').trim()) { alert('Precisa de um e-mail.'); return; }
    if (!window.confirm(`Criar acesso para ${inv.nome} com o e-mail ${email}? Ele vai poder completar o próprio cadastro e os cartões.`)) return;
    try {
      const r = await api(`/investidores/${inv.id}/criar-acesso`, { method: 'POST', body: JSON.stringify({ email }) });
      setConvResult({ ...r, nome: inv.nome });
      carregar();
    } catch (e) { alert(e.message); }
  };
  const abrir = async (inv) => {
    try {
      const d = await api(`/investidores/${inv.id}`); setDetalhe(d);
      const a = {}; d.cartoes.filter(c => c.status === 'AGUARDANDO_APROVACAO').forEach(c => { a[c.id] = true; }); setAprov(a);
      setOperacaoAprov(d.operacao_sugerida ? String(d.operacao_sugerida) : '');
    } catch (e) { alert(e.message); }
  };
  const aprovar = async () => {
    const pend = detalhe.cartoes.filter(c => c.status === 'AGUARDANDO_APROVACAO');
    const ok = pend.filter(c => aprov[c.id]).map(c => c.id), nok = pend.filter(c => !aprov[c.id]).map(c => c.id);
    if (!operacaoAprov) { alert('Escolha a operação do investidor antes de aprovar.'); return; }
    const nomeOp = (detalhe.operacoes || []).find(o => String(o.id) === operacaoAprov)?.nome || operacaoAprov;
    if (!window.confirm(`Aprovar cadastro de ${detalhe.titular.nome} na operação "${nomeOp}": ${ok.length} cartão(ões) aprovado(s), ${nok.length} recusado(s)?`)) return;
    try { await api(`/investidores/${detalhe.id || detalhe.titular.id}/aprovar`, { method: 'POST', body: JSON.stringify({ cartoes_aprovados: ok, cartoes_recusados: nok, operacao_id: Number(operacaoAprov) }) }); setDetalhe(null); carregar(); }
    catch (e) { alert(e.message); }
  };
  const salvarTermo = async (grupo) => {
    try { const r = await api('/termo', { method: 'PUT', body: JSON.stringify({ grupo, texto: termos[grupo] }) }); setTermoInfo(t => ({ ...t, [grupo]: r })); alert('Termo salvo.'); }
    catch (e) { alert(e.message); }
  };
  const copiar = (txt) => { navigator.clipboard?.writeText(txt).then(() => alert('Link copiado.')).catch(() => window.prompt('Copie o link:', txt)); };
  if (erro) return <div style={{ color: colors.danger }}>{erro}</div>;
  if (!lista) return <LoadingSpinner />;
  const t = detalhe?.titular;
  return (
    <div>
      <BlocoD4Sign podeEditar={podeEditar} />
      {podeEditar && (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, color: colors.text, marginBottom: 10 }}>Convidar investidor</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1.2fr 2fr 0.8fr auto', gap: 10, alignItems: 'end' }}>
            <div><label style={{ fontSize: 11, color: colors.textMuted }}>Nome</label><input style={inp} value={conv.nome} onChange={e => setConv(c => ({ ...c, nome: e.target.value }))} placeholder="NOME COMPLETO" /></div>
            <div><label style={{ fontSize: 11, color: colors.textMuted }}>E-mail (vira o login)</label><input style={inp} value={conv.email} onChange={e => setConv(c => ({ ...c, email: e.target.value }))} /></div>
            <div><label style={{ fontSize: 11, color: colors.textMuted }}>WhatsApp</label><input style={inp} value={conv.telefone} onChange={e => setConv(c => ({ ...c, telefone: e.target.value }))} /></div>
            <div><label style={{ fontSize: 11, color: colors.textMuted }}>Modalidade</label>
              <select style={inp} value={conv.modalidade} onChange={e => setConv(c => ({ ...c, modalidade: e.target.value, taxa_pct: taxaSugerida(e.target.value, c.pontos_organicos_investidor) }))}>
                {Object.entries(MODALIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select></div>
            <div><label style={{ fontSize: 11, color: colors.textMuted }}>Taxa %</label><input style={inp} value={conv.taxa_pct} onChange={e => setConv(c => ({ ...c, taxa_pct: e.target.value }))} /></div>
            <Button onClick={convidar} disabled={busy || !conv.nome || !conv.email} color={colors.primary}>Convidar</Button>
          </div>
          {(
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13, color: colors.text, cursor: 'pointer' }}>
              <input type="checkbox" checked={conv.pontos_organicos_investidor}
                onChange={e => setConv(c => ({ ...c, pontos_organicos_investidor: e.target.checked, taxa_pct: taxaSugerida(c.modalidade, e.target.checked) }))} />
              Pontos orgânicos do cartão ficam com o investidor (no mútuo sugere 1%; com a operação, 2%; vale pra SCP também)
            </label>
          )}
          {convResult && (
            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: `${colors.success}12`, border: `1px solid ${colors.success}44`, fontSize: 13, color: colors.text }}>
              Convite gerado pra <b>{convResult.login}</b>. {convResult.enviado_por_email ? 'E-mail enviado.' : 'E-mail não configurado — mande o link pelo WhatsApp:'}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                <code style={{ fontSize: 11, wordBreak: 'break-all', color: colors.info }}>{convResult.link}</code>
                <Button size="sm" variant="outline" onClick={() => copiar(convResult.link)}>Copiar</Button>
              </div>
              <div style={{ fontSize: 11, color: colors.textDim, marginTop: 4 }}>Válido por 72 horas. Ele cria a senha, entra e preenche o cadastro.</div>
              {/* Sem operação ele só aparece pro ADMIN — e a equipe descobre
                  dias depois, achando que o cadastro sumiu. */}
              {convResult.aviso_operacao && (
                <div style={{ marginTop: 8, padding: 8, borderRadius: 6, background: `${colors.warning}15`, border: `1px solid ${colors.warning}55`, fontSize: 12 }}>
                  {convResult.aviso_operacao}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={verInativos} onChange={e => setVerInativos(e.target.checked)} />
          Mostrar investidores inativos
        </label>
        <span style={{ fontSize: 11, color: colors.textDim }}>
          {lista.length} na lista{verInativos ? ' (incluindo inativos)' : ''}
        </span>
      </div>
      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 14 }}>
        {lista.length === 0 ? <EmptyState message="Nenhum investidor convidado ainda" /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
              <th style={thS}>Investidor</th><th style={thS}>Login</th><th style={thS}>Modalidade</th><th style={{ ...thS, textAlign: 'right' }}>Taxa</th>
              <th style={thS}>Cadastro</th><th style={thS}>Cartões</th><th style={thS}>Ações</th>
            </tr></thead>
            <tbody>{lista.map(inv => (
              <tr key={inv.id} style={{ borderBottom: `1px solid ${colors.border}22`, opacity: inv.status === 'INATIVO' ? 0.55 : 1 }}>
                <td style={{ ...tdS, fontWeight: 600 }}>{inv.nome}
                  {inv.status === 'INATIVO' && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4, background: `${colors.textDim}33`, color: colors.textMuted, fontWeight: 700 }}>INATIVO</span>}
                  <div style={{ fontSize: 11, color: colors.textDim, fontWeight: 400 }}>{inv.telefone || ''}</div></td>
                <td style={{ ...tdS, fontSize: 12 }}>{inv.usuario_login || '—'}
                  <div style={{ fontSize: 10, color: colors.textDim }}>
                    {inv.qtd_usuarios > 1 && <span style={{ color: colors.info }}>{inv.qtd_usuarios} logins · </span>}
                    {inv.ultimo_acesso ? `último acesso ${new Date(inv.ultimo_acesso).toLocaleDateString('pt-BR')}` : (inv.convite_valido ? 'convite válido' : 'nunca entrou')}
                  </div></td>
                <td style={{ ...tdS, fontSize: 12 }}>{MODALIDADE_LABEL[inv.modalidade] || '—'}<div style={{ fontSize: 10, color: colors.textDim }}>{pontosTxt(inv)}</div></td>
                <td style={{ ...tdS, textAlign: 'right' }}>{pct(inv.taxa_pct)}</td>
                <td style={tdS}><Badge text={inv.cadastro_status || 'SEM CADASTRO'} color={CAD_COR[inv.cadastro_status] || colors.textMuted} />
                  {inv.cadastro_enviado_em && <div style={{ fontSize: 10, color: colors.textDim }}>enviado {new Date(inv.cadastro_enviado_em).toLocaleDateString('pt-BR')}</div>}
                  {/* Sem esses campos o contrato sai com espaços em branco. */}
                  {(inv.falta_para_contrato || []).length > 0 && (
                    <div style={{ fontSize: 10, color: colors.warning, marginTop: 2 }}>
                      falta p/ contrato: {inv.falta_para_contrato.join(', ')}
                    </div>
                  )}</td>
                <td style={{ ...tdS, fontSize: 12 }}>{inv.cartoes_ativos} ativo(s){inv.cartoes_pendentes > 0 && <span style={{ color: colors.warning }}> · {inv.cartoes_pendentes} aguardando</span>}</td>
                <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                  <Button size="sm" variant="outline" color={colors.info} onClick={() => abrir(inv)}>{inv.cadastro_status === 'ENVIADO' ? 'Revisar e aprovar' : 'Ver cadastro'}</Button>
                  {podeEditar && inv.cadastro_status === 'CONVIDADO' && inv.tem_acesso && <span style={{ marginLeft: 6 }}><Button size="sm" variant="outline" onClick={() => reenviar(inv)}>Reenviar convite</Button></span>}
                  {podeEditar && !inv.tem_acesso && <span style={{ marginLeft: 6 }}><Button size="sm" variant="outline" color={colors.success} onClick={() => criarAcesso(inv)}>Criar acesso</Button></span>}
                  {podeEditar && (
                    <span style={{ marginLeft: 6 }}>
                      <Button size="sm" variant="outline" color={inv.status === 'INATIVO' ? colors.success : colors.warning}
                        onClick={() => alternarAtivo(inv)}>
                        {inv.status === 'INATIVO' ? 'Reativar' : 'Desativar'}
                      </Button>
                    </span>
                  )}
                  {isAdmin() && <span style={{ marginLeft: 6 }}><Button size="sm" variant="outline" color={colors.danger} onClick={() => excluirInvestidor(inv)}>Excluir</Button></span>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
      {podeEditar && (
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontWeight: 700, color: colors.text, marginBottom: 4 }}>Termos que o investidor aceita</div>
          <div style={{ fontSize: 12, color: colors.textMuted, marginBottom: 10 }}>Cole o contrato de cada modalidade. Marcadores substituídos na hora: {['NOME', 'CPF', 'EMAIL', 'TELEFONE', 'ENDERECO', 'TAXA', 'DATA', 'LIMITE_TOTAL', 'CLAUSULA_PONTOS_ORGANICOS'].map(k => <code key={k} style={{ marginRight: 4 }}>{'{' + k + '}'}</code>)} — a cláusula dos pontos orgânicos muda sozinha conforme a escolha do investidor. O aceite guarda data, hora, IP e a versão do texto.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {['TAXA', 'SCP'].map(g => (
              <div key={g}>
                <div style={{ fontSize: 12, fontWeight: 700, color: colors.text, marginBottom: 4 }}>{g === 'TAXA' ? 'Mútuo mercantil (taxa sobre a utilização)' : 'SCP (divide lucro)'}
                  <span style={{ fontWeight: 400, color: colors.textDim, marginLeft: 8 }}>{termoInfo[g]?.padrao ? 'texto provisório' : `atualizado por ${termoInfo[g]?.atualizado_por || '—'}`}</span></div>
                <textarea value={termos[g]} onChange={e => setTermos(t => ({ ...t, [g]: e.target.value }))} rows={12} style={{ ...inp, fontFamily: 'inherit', fontSize: 12, resize: 'vertical' }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}><Button size="sm" color={colors.primary} onClick={() => salvarTermo(g)}>Salvar termo</Button></div>
              </div>
            ))}
          </div>
        </div>
      )}
      <Modal isOpen={!!detalhe} onClose={() => setDetalhe(null)} title={t ? `Cadastro — ${t.nome}` : ''} width={820}>
        {t && (
          <div style={{ fontSize: 13, color: colors.text }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
              {[['CPF', t.cpf], ['Nascimento', t.data_nascimento], ['WhatsApp', t.telefone], ['E-mail', t.email],
                ['Endereço', [t.endereco_logradouro, t.endereco_numero, t.endereco_complemento].filter(Boolean).join(', ')],
                ['Cidade/UF', [t.endereco_bairro, t.endereco_cidade, t.endereco_uf].filter(Boolean).join(' · ')], ['CEP', t.cep],
                ['PIX', [t.pix_tipo, t.pix_chave].filter(Boolean).join(': ')], ['Banco/ag/conta', [t.banco_recebimento, t.agencia_recebimento, t.conta_recebimento].filter(Boolean).join(' · ')],
                ['Modalidade', MODALIDADE_LABEL[detalhe.modalidade] || '—'], ['Taxa', pct(detalhe.taxa_pct) + ' · ' + (pontosTxt(detalhe) || 'SCP')],
                ['Aceite do termo', t.termo_aceite_em ? `${new Date(t.termo_aceite_em).toLocaleString('pt-BR')} · IP ${t.termo_aceite_ip || '?'}` : '—'],
              ].map(([k, v]) => <div key={k}><div style={{ fontSize: 10, color: colors.textMuted, textTransform: 'uppercase' }}>{k}</div><div>{v || '—'}</div></div>)}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
              <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                <th style={thS}>Aprovar</th><th style={thS}>Cartão</th><th style={thS}>Bandeira</th><th style={thS}>Final</th><th style={{ ...thS, textAlign: 'right' }}>Limite</th><th style={thS}>Venc./Fech.</th><th style={thS}>Situação</th>
              </tr></thead>
              <tbody>{detalhe.cartoes.map(c => (
                <tr key={c.id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                  <td style={tdS}>{c.status === 'AGUARDANDO_APROVACAO' ? <input type="checkbox" checked={!!aprov[c.id]} onChange={e => setAprov(a => ({ ...a, [c.id]: e.target.checked }))} /> : '—'}</td>
                  <td style={tdS}>{c.nome_cartao}<div style={{ fontSize: 10, color: colors.textDim }}>{c.banco || ''}</div></td>
                  <td style={tdS}>{c.bandeira || '—'}</td><td style={tdS}>{c.numeracao_final || '—'}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{fmt(c.limite_total)}</td>
                  <td style={tdS}>{c.data_vencimento ? `dia ${String(c.data_vencimento).slice(-2)}` : '—'}{c.dia_fechamento ? ` / ${c.dia_fechamento}` : ''}</td>
                  <td style={tdS}><Badge text={c.status} color={c.status === 'AGUARDANDO_APROVACAO' ? colors.warning : (STATUS_COR[c.status] || colors.textMuted)} /></td>
                </tr>
              ))}</tbody>
            </table>
            <ContratosD4Sign titularId={t.id} podeEditar={podeEditar} />
            {/* Quem aprova escolhe a operação (04/11/2026). pendente_revisao
                cobre o investidor que reenviou o cadastro já aprovado — sem
                ele o REVISAR ficava aceso sem botão nenhum pra apagá-lo. */}
            {(() => {
              const aprovavel = podeEditar && (t.cadastro_status === 'ENVIADO' || t.pendente_revisao
                || detalhe.cartoes.some(c => c.status === 'AGUARDANDO_APROVACAO'));
              return (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {aprovavel && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: colors.textMuted, marginRight: 'auto' }}
                      title="A operação decide quem da gestão enxerga este investidor.">
                      Operação
                      <select value={operacaoAprov} onChange={e => setOperacaoAprov(e.target.value)} style={{ ...inp, width: 'auto', minWidth: 180 }}>
                        <option value="">— escolha —</option>
                        {(detalhe.operacoes || []).map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
                      </select>
                    </label>
                  )}
                  <Button variant="outline" onClick={() => setDetalhe(null)}>Fechar</Button>
                  {aprovavel && (
                    <Button color={colors.success} icon={CheckCircle2} onClick={aprovar} disabled={!operacaoAprov}>Aprovar cadastro</Button>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ── Página ───────────────────────────────────────────────────────────────────
const ABAS = [
  { id: 'apuracao', label: 'Apuração', icon: Wallet },
  { id: 'taxas', label: 'Taxas', icon: Percent },
  { id: 'historico', label: 'Histórico', icon: History },
  { id: 'investidores', label: 'Investidores', icon: Users },
];

export default function RemuneracaoPage() {
  const podeEditar = podeGerenciarCartoes();
  const [aba, setAba] = useState('apuracao');
  const [mes, setMes] = useState(mesAtual());
  const [titularSel, setTitularSel] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [demo, setDemo] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true); setErro('');
    try { setData(await api(`/competencia?mes=${mes}`)); } catch (e) { setErro(e.message); setData(null); }
    setLoading(false);
  }, [mes]);
  useEffect(() => { if (aba === 'apuracao') carregar(); }, [aba, carregar]);

  const abrirDemonstrativo = async (id) => {
    try { setDemo(await api(`/fechamentos/${id}`)); } catch (e) { alert(e.message); }
  };

  const investidores = (data?.investidores || []).filter(i => !titularSel || String(i.titular_id) === titularSel);
  const r = data?.resumo;

  return (
    <div>
      <PageHeader title="Remuneração de Investidor" subtitle="Taxa sobre o limite comprometido de cada cartão, por competência de vencimento" />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {ABAS.map(t => (
          <button key={t.id} onClick={() => setAba(t.id)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, cursor: 'pointer',
            border: `1px solid ${aba === t.id ? colors.primary : colors.border}`,
            background: aba === t.id ? `${colors.primary}18` : colors.card, color: aba === t.id ? colors.primary : colors.textMuted,
            fontSize: 13, fontWeight: 600,
          }}><t.icon size={14} /> {t.label}</button>
        ))}
      </div>

      {aba === 'taxas' && <AbaTaxas podeEditar={podeEditar} />}
      {aba === 'investidores' && <AbaInvestidores podeEditar={podeEditar} />}
      {aba === 'historico' && <AbaHistorico podeEditar={podeEditar} onDemonstrativo={abrirDemonstrativo} onChange={() => {}} />}

      {aba === 'apuracao' && (<>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: colors.textMuted }}>Competência</label>
          <input type="month" value={mes} onChange={e => setMes(e.target.value)} style={{ ...inp, width: 160 }} />
          <select value={titularSel} onChange={e => setTitularSel(e.target.value)} style={{ ...inp, width: 220 }}>
            <option value="">Todos os investidores</option>
            {(data?.investidores || []).map(i => <option key={i.titular_id} value={i.titular_id}>{i.nome}</option>)}
          </select>
          <Button size="sm" variant="outline" icon={RefreshCw} onClick={carregar} disabled={loading}>Recarregar</Button>
        </div>
        {erro && <div style={{ color: colors.danger, marginBottom: 12 }}>{erro}</div>}
        {loading && !data ? <LoadingSpinner /> : data && (<>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
            <Kpi label="Limite comprometido total" valor={fmt(r.base_total)} />
            <Kpi label="Rendimento a pagar" valor={fmt(r.rendimento_total)} cor={colors.success} sub={`${r.fechados} fechado(s) · ${r.pagos} pago(s)`} />
            <Kpi label="Investidores / cartões" valor={`${r.investidores} / ${r.cartoes}`} />
            <Kpi label="Faturas pendentes" valor={r.pendentes} cor={r.pendentes ? colors.warning : colors.text} sub="sem valor lançado" />
            <Kpi label="Em revisar" valor={r.revisar} cor={r.revisar ? colors.danger : colors.text} sub="ver motivo na linha" />
          </div>
          {investidores.length === 0 ? (
            <EmptyState message="Nenhum investidor com taxa cadastrada no seu escopo — cadastre na aba Taxas" />
          ) : investidores.map(inv => (
            <InvestidorBloco key={inv.titular_id} inv={inv} mes={mes} podeEditar={podeEditar} onChange={carregar} onDemonstrativo={abrirDemonstrativo} />
          ))}
        </>)}
      </>)}

      <Modal isOpen={!!demo} onClose={() => setDemo(null)} title={demo ? `Demonstrativo — ${demo.titular_nome} · ${mesBR(demo.competencia)}` : ''} width={860}>
        {demo && (
          <div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              <Kpi label="Rendimento" valor={fmt(demo.rendimento)} cor={colors.success} sub={`base ${fmt(demo.base_total)} × ${pct(demo.taxa_pct)}`} />
              <Kpi label="Pagamento" valor={dataBR(demo.data_pagamento)} sub={demo.status === 'PAGO' ? `pago em ${dataBR(demo.pago_em)}` : 'a pagar'} />
              <Kpi label="Cartões" valor={demo.itens.length} />
            </div>
            <div style={{ overflowX: 'auto', marginBottom: 14 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <th style={thS}>Cartão</th><th style={thS}>Venc.</th><th style={{ ...thS, textAlign: 'right' }}>Fatura</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Saldo futuro</th><th style={{ ...thS, textAlign: 'right' }}>Base</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Limite comprometido</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Demonstr.</th><th style={thS}>Conferência</th>
                </tr></thead>
                <tbody>{demo.itens.map(c => (
                  <tr key={c.cartao_id} style={{ borderBottom: `1px solid ${colors.border}22` }}>
                    <td style={tdS}>{c.nome_cartao}<div style={{ fontSize: 10, color: colors.textDim }}>{[c.banco, c.final && `final ${c.final}`].filter(Boolean).join(' · ')}</div></td>
                    <td style={tdS}>{dataBR(c.data_vencimento)}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(c.valor_fatura)}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(c.saldo_parcelado_futuro)}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(c.base_cartao)}</td>
                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{fmt(c.limite_comprometido ?? c.base_cartao)}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(c.rendimento_cartao)}</td>
                    <td style={tdS}><Badge text={c.status} color={STATUS_COR[c.status] || colors.textMuted} />{c.observacao && <span style={{ fontSize: 11, color: colors.textMuted, marginLeft: 6 }}>{c.observacao}</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outline" icon={X} onClick={() => setDemo(null)}>Fechar</Button>
              <Button icon={FileText} color={colors.primary} onClick={() => imprimirDemonstrativo(demo)}>Imprimir / salvar PDF</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
